import { useState } from "react";
import {  Search, ShieldCheck } from "lucide-react";

import Container from "../../Components/Container/Container";
import Seo from "../../Components/SEO/Seo";

import "../../Style/legal/legal.css";

const DATA_PROCESSING_REGISTRY = [
  {
    dataset: "Customer Details",
    source: "Customer",
    purpose: "To keep customers informed & educated as well as deliver the services contracted to provide",
    description: "Company name, address, phone number, software installed. Contact names, email, phone, communications, meeting/call notes, support tickets",
    storage: "Secure CRM / Database",
    basis: "Contractual",
    retention: "1 year of non-use unless unsubscribe or right to be forgotten is received",
    security: "Protected to ISO 27001 standards",
  },
  {
    dataset: "Customer Communications & Event Bookings",
    source: "Customer",
    purpose: "To send emails to customers and process event/webinar bookings",
    description: "Contact name, email address, and company name",
    storage: "Authorized Marketing Engine",
    basis: "Contractual",
    retention: "1 year of non-use unless unsubscribe or right to be forgotten is received",
    security: "Protected to ISO 27001 standards & GDPR Processor agreement in place",
  },
  {
    dataset: "Prospective Customer Details",
    source: "Data purchase, website enquiries, exhibitions/events, direct calling",
    purpose: "To market to and inform potential customers about solutions in our key areas of expertise",
    description: "Company name, address, phone number. Contact names, email, phone, communications, meeting & call notes",
    storage: "Secure CRM / Database",
    basis: "Legitimate Interest",
    retention: "1 year of non-use unless unsubscribe or right to be forgotten is received",
    security: "Protected to ISO 27001 standards; clear opt-out available at all times",
  },
  {
    dataset: "Prospective Customer Event & Email Records",
    source: "Website enquiries, exhibitions, direct outreach",
    purpose: "To email prospective customers and process event registrations",
    description: "Contact name, email address, and company name",
    storage: "Authorized Marketing Engine",
    basis: "Legitimate Interest",
    retention: "1 year of non-use unless unsubscribe or right to be forgotten is received",
    security: "Protected to ISO 27001 standards & GDPR Processor agreement in place",
  },
  {
    dataset: "Physical Direct Mail Contact Details",
    source: "Exported CRM / Validated Registries",
    purpose: "Third-party mailing execution for customer and prospective customer physical mailings",
    description: "Exported list containing contact name, company name, and postal address details",
    storage: "Certified Third-Party Mailing Partners",
    basis: "Contractual & Legitimate Interest",
    retention: "Permanently deleted by third-party mailing houses directly after execution of agreed mailing",
    security: "Protected to ISO 27001 standards & signed GDPR Processor agreements",
  },
  {
    dataset: "Web Chat Conversation Records",
    source: "Customer / Prospective Customer",
    purpose: "To respond to click-to-chat inquiries from customers/prospective customers",
    description: "Customer/prospective customer name, email address, and inquiry details",
    storage: "Live Chat Application",
    basis: "Contractual & Legitimate Interest",
    retention: "5 years",
    security: "Protected to ISO 27001 standards",
  },
  {
    dataset: "Webform Inquiries",
    source: "Customer / Prospective Customer",
    purpose: "To respond to inquiries from website visitors when they fill in a form on our website",
    description: "Customer/prospective customer name, email address, and inquiry details",
    storage: "Web Database & CRM",
    basis: "Contractual & Legitimate Interest",
    retention: "5 years",
    security: "Protected to ISO 27001 standards",
  },
];

export default function Privacy() {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredRegistry = DATA_PROCESSING_REGISTRY.filter(
    (item) =>
      item.dataset.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.purpose.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.basis.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.source.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="legal-page">
      <Seo
        title="Privacy Policy & GDPR Compliance | Conectify Tech"
        description="Conectify Tech's Privacy Policy, GDPR compliance framework, data subject rights, and data processing registry."
      />

      <Container className="legal-container">
        <header className="legal-header">
          <span className="section-eyebrow eyes">
            <ShieldCheck size={14} className="inline mr-1" /> Data Governance
          </span>
          <h1 className="legal-header__title">Privacy Policy</h1>
          <p className="legal-header__date">Last Updated: September 2026</p>
        </header>

        <article className="legal-card legal-content">
          <p>
            <strong>Conectify Tech</strong> (“Conectify Tech”, “we”, “us”, “ours”) realizes the importance of the
            privacy of our users. This Privacy Policy governs how we collect, use, maintain, disclose, or secure
            information which is collected from our users (“Users”) of{" "}
            <a href="https://conectify-tech.com/" target="_blank" rel="noopener noreferrer">
              https://conectify-tech.com/
            </a>. This privacy policy applies to the website and all products and services offered by Conectify Tech.
            This Privacy Policy was prepared because we believe you should know as much as possible about Conectify Tech’s
            practices. It describes the information we collect on, or make available through, Conectifytech.com (the “Site”)
            for our products or services that are governed by terms of use that expressly reference this Privacy Policy
            (the “Service”), what may happen to that information, the security measures that we use, your rights with
            respect to your information stored or displayed in or through the Conectify Tech Service and collected in
            connection with your use of the Service or the development of our database and Service. The use of information
            collected by our Service shall be limited to the purpose of providing the Service for which you have engaged
            us. Any capitalized terms not defined in this Privacy Policy are defined in the Terms of Use. We participate in
            and comply with the EU-U.S. Privacy Shield and Swiss-U.S. Privacy Shield Frameworks to cover data we process
            from Europe.
          </p>

          <p>
            If you are a California resident, please see our Notice To California Residents to learn more about your
            rights under the California Consumer Privacy Act (the “CCPA”).
          </p>

          <h2>Information We Collect for Our Database</h2>
          <p>
            Conectify Tech develops and maintains a database (the “Database”) of company and individual profiles
            (“Profiles”). Information collected for the development of an individual Profile and/or published in an
            individual Profile on the Conectify Tech Service may include: First Name, Last Name, Company Name, Website,
            Personal Email, Physical Address, Phone Number, Fax Number, City, State, Zip Codes, Title, Industry, Employee
            Size, links to social media postings by or about the individual, professional contact information, and
            similar information relating to the professional sphere of an individual.
          </p>
          <p>
            Information collected for the development of a company Profile and/or published in a company Profile on
            Conectify Tech’s database may include: the name and title of the company’s officers and certain company’s
            employees, information obtained from the company or published by third parties.
          </p>

          <h2>Our Sources of Information</h2>
          <p>
            Conectify Tech collects information about individuals and companies from a variety of publicly available and
            other sources, such as company websites, company blogs, non-confidential corporate records or reports (for
            example SEC filings, commercial registers), press releases, news articles, magazines, social media sites, and
            other content published by or on behalf of a company or published by data brokers or third parties in
            accordance with their terms and conditions for the collection, disclosure, and sharing of information.
          </p>
          <p>
            Conectify Tech also collects information about individuals and companies from government publications in
            accordance with their terms and conditions for the disclosure and sharing of information.
          </p>
          <p>
            Finally, Conectify Tech also receives some information directly from the individual or company whose Profile
            is featured in our Database. Subscribers who know facts about a particular individual or company may
            contribute some information to our Database about that individual (name, title, email, phone, social profile
            link) or company (name, URL, address, phone, industry, revenue, employee count) if they desire. This
            information provided is only used for the sole purpose of completing your request or for whatever reason it
            may have been provided. Conectify Tech confirms where possible accuracy of information provided as described
            above by individuals, companies, subscribers, and other third parties before it is added to the Database and
            Profiles.
          </p>

          <h2>How We Use the Information We Collect</h2>
          <p>
            This information collected as described above is then compiled, edited, and organized through Conectify Tech’s
            unique proprietary sales and marketing intelligence application to create a Profile for each individual or
            company.
          </p>
          <p>
            Each individual or company Profile is integrated into the Database, which is part of Conectify Tech’s
            Service. Clients of Conectify Tech’s Service have access to the Database and the Profiles and, as such,
            might conduct searches and save the identified Profiles meeting their search criteria for their own internal
            purposes. Clients might also contact individuals or companies using the contact information contained in
            Profiles. For example, a Conectify Tech client might use the Database in order to identify the specific
            individual who may be a decision maker in a company, and then contact the individual in order to offer their
            goods or services for sale to the company. Limited access to the Database is provided at no cost, whereas full
            access to the Database is subject to the payment of a subscription fee.
          </p>

          <h2>Removal of Information</h2>
          <p>
            If there is any PII you may wish to be removed from our Platform, please contact us at{" "}
            <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>. Submission of removal required must have
            your information exactly as it is listed. Please be aware that removal requests are not processed
            instantaneously. There may be a reasonable delay in removing any information requested. Additionally, as
            Conectify Tech collects data from multiple sources a single removal request may not eliminate all your
            personal information listed on our Platform or Database. Therefore, you may be required to submit multiple
            requests. If your information repeatedly reappears please contact us at{" "}
            <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
          </p>
          <div className="legal-callout">
            When sending an email for removal of information, please clearly title the email as the following:
            <br />
            <strong>Subject: </strong>Removal of Information Request – [Your Full Name] and [Account Name]
          </div>

          <h2>Cookies and Other Tracking</h2>
          <p>
            Yes, we do use cookies. Cookies are small files that are transferred to your system’s hard drive through the
            browser (if allowed) which enables the site to recognize your browser and retain certain information. We use
            cookies to help us remember your preferences based on your previous or current site activity to provide you
            with better services. We also use cookies to help us compile aggregate data about site traffic and site
            interaction so that we can offer better site experiences and tools in the future. Please note that if you
            turn your cookies off, certain features on our site may be disabled. This will affect your browsing experience
            and the website may not function properly.
          </p>

          <h2>Third Party Access to Your Information</h2>
          <p>
            Conectify Tech uses third party vendors and hosting partners for the necessary software, hardware, storage,
            and networking which is required to run our platform. Throughout the course of our provision of our services to
            you, we may delegate our authority to collect, access, use, and disseminate your information. It is therefore
            necessary that you grant the third parties we may use during our business the same rights that you afford us
            under this Privacy Policy. For this reason, you hereby agree that for every authorization which you grant to us
            in this Privacy Policy, you also grant to any third party that we may hire, contract, or otherwise retain the
            services of for the purpose of operating, maintaining, repairing, or otherwise improving or preserving our
            Platform or Database or any underlying files or systems. You agree not to hold Conectify Tech liable for the
            actions of any of these third parties, even if we would normally be held vicariously liable for their
            actions, and that you must take legal action against them directly should they commit any tort or other
            actionable wrong against you.
          </p>

          <h2>Law Enforcement</h2>
          <p>
            Conectify Tech may disclose your information to law enforcement authorities if compelled to do so by a court
            order. Additionally, you agree that we may disclose your information if we reasonably believe that you have
            violated any US laws or the terms of any of our agreements with you or if we believe that a third party is at
            risk of bodily harm. Additionally, we are not obligated to notify you of a subpoena affecting your privacy that
            we receive. We may also proactively report you and release your information without receiving any request to
            third parties where we believe that it is proper to do so for legal reasons, where your actions violate any
            laws of the US or any other country having jurisdiction over us, our Site, or the Platform. You release us
            from any damages that may arise from or relate to the release of your information to a request from law
            enforcement agencies or private litigants. We may release your information under the conditions listed in this
            paragraph whether it is to individuals or entities and to any state or national authorities, as required.
          </p>

          <h2>Third Parties</h2>
          <p>
            Conectify Tech may post links to third party websites, which may include information that we have no control
            over. When accessing a third-party site through our Platform or Database, you acknowledge that you are aware
            that these third-party websites are not screened for privacy or security issues by us, and you release us from
            any liability for the conduct of these third-party websites.
          </p>
          <p>
            Additionally, Conectify Tech bears no responsibility for the information collected or used by any advertiser
            or third-party website. You must review their Terms of Service and Privacy Policy to understand how their
            information collection practices work.
          </p>

          <h2>Security</h2>
          <p>
            We scan our Master Database on a regular basis to identify any vulnerabilities or security fallouts to make
            your visit to our data safe. All your personal information is secured in networks and accessible by only a
            limited number of personnel with special access or Third Parties who are in a binding agreement with Conectify
            Tech.
          </p>

          <h2>Children’s Privacy</h2>
          <p>
            We intend to fully comply with all US and international laws respecting children’s privacy. Therefore,
            Conectify Tech does not collect or process PII for any persons aged under 13. If you are under 13, please do
            not access the Data.
          </p>

          <h2>International Transfer of Information</h2>
          <p>
            Your information may be transferred to – and maintained on – computers located outside of your state,
            province, country, or other governmental jurisdiction where the privacy laws may not be as protective as those
            in your jurisdiction. We may transfer personal information to the US or elsewhere and process it there. Your
            consent to this Privacy Policy followed by your submission of such information represents your agreement to
            that transfer.
          </p>

          <h2>Merger and Acquisition</h2>
          <p>
            If Conectify Tech is involved in a bankruptcy, merger, acquisition, reorganization, or sale of assets, your
            information may be sold or transferred as part of that transaction. Please be aware that once the information
            is transferred your privacy rights may change.
          </p>

          <h2>Policy Amendments</h2>
          <p>
            Like our Terms of Service, Conectify Tech may amend this Privacy Policy from time to time. With every updation
            of this Privacy Policy, Conectify Tech will modify the date listed on this Agreement or we may contact you.
            You must agree to the amendments as a condition of your continued use of our Data or Platform. If you do not
            agree, you must immediately cease using our Data and Platform and notify us of your refusal to agree by
            e-mailing us at <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
          </p>

          <h2>GDPR Compliance for European Citizens</h2>
          <p>
            Conectify Tech respects the rights of citizens living within the European Economic Community and the rights
            afforded to them under the General Data Protection Regulation (“GDPR”), the sections 18-23 are referred to as
            our Privacy Notice and addresses additional privileges that EEC users may have under our Privacy Policy. The
            GDPR affords the following rights to EEC citizens and persons residing in EEC territories:
          </p>
          <ul>
            <li>
              <strong>a) The right to be informed:</strong> You have the right to be informed as to what Conectify Tech
              does with your personal information which is why we work hard to be transparent about how we use your data.
            </li>
            <li>
              <strong>b) The right to access:</strong> You have the right to access your information any time and can do
              so by contacting us at <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
            </li>
            <li>
              <strong>c) The right to rectification:</strong> If you believe the information Conectify Tech has about you
              is incorrect/requires updating, it is within your rights under the GDPR to ask us to rectify it. You can do
              so by contacting us at <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
            </li>
            <li>
              <strong>d) The right to erasure:</strong> You have the right to ask Conectify Tech to erase personal
              information that we hold on you. Please contact us on{" "}
              <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
            </li>
            <li>
              <strong>e) The right to restrict processing:</strong> You can ask Conectify Tech to restrict processing of
              your data which means we can only store your PII, but not process it. Please note that this means we will
              retain your data to comply with future/additional requests.
            </li>
            <li>
              <strong>f) The right to object:</strong> Regardless of the basis of data processing (legitimate interest),
              you can object to Conectify Tech processing your data by contacting us.
            </li>
            <li>
              <strong>g) The right to withdraw consent:</strong> You have the right to revoke prior permission given to
              Conectify Tech to process your PII and withdraw consent any time. Please contact us if you wish to do so.
            </li>
          </ul>

          <h2>Basis for Collecting your Personally Identifiable Information (PII)</h2>
          <p>
            Conectify Tech can collect your PII for Legitimate Purposes which include, but are not limited to:
          </p>
          <div className="legal-points">
            <p>
              <strong>a) Contractual purposes:</strong>
            </p>
            <ul>
              <li>(1) to manage a contract between Conectify Tech and User,</li>
              <li>(2) to process billing information, and</li>
              <li>(3) to communicate with the User.</li>
            </ul>

            <p>
              <strong>b) Services related to Platform and Site:</strong>
            </p>
            <ul>
              <li>(1) set up and manage the Users Conectify Tech account,</li>
              <li>(2) in the course of related customer services, and</li>
              <li>
                (3) deliver a better experience of Conectify Tech’s Database and Platform by understanding your interests
                and needs regarding the same.
              </li>
            </ul>

            <p>
              <strong>c) Sharing with Third Parties which include:</strong>
            </p>
            <ul>
              <li>(1) partners and affiliates for various purposes such as third party storage,</li>
              <li>(2) for email delivery purposes,</li>
              <li>
                (3) for payment processing. You can request a list of Third Parties we work with by contacting us at{" "}
                <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
              </li>
            </ul>

            <p>
              <strong>d) Other purposes including:</strong>
            </p>
            <ul>
              <li>(1) for staff training purposes,</li>
              <li>(2) to improve Platform and Site experience,</li>
              <li>(3) direct marketing.</li>
            </ul>
          </div>

          <h2>Data Processing Inventory under GDPR</h2>
          <p>
            The table below documents the datasets collected across our platform, their legal basis for processing,
            operational purposes, storage repositories, and retention durations:
          </p>

          {/* Search Filter Bar */}
          <div className="legal-search-bar" style={{ margin: "1.25rem 0" }}>
            <div style={{ position: "relative", maxWidth: "420px" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-muted)",
                }}
              />
              <input
                type="text"
                placeholder="Filter by dataset, purpose, or legal basis..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px 10px 38px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color, rgba(226, 232, 240, 0.8))",
                  background: "var(--bg-secondary, #f8fafc)",
                  color: "var(--text-primary)",
                  fontSize: "0.875rem",
                  outline: "none",
                }}
              />
            </div>
          </div>

          {/* Responsive High-Readability Table Container */}
          <div className="legal-table-wrapper">
            <div className="legal-table-scroll">
              <table className="gdpr-table">
                <thead>
                  <tr>
                    <th style={{ width: "16%" }}>Data Set</th>
                    <th style={{ width: "12%" }}>Source</th>
                    <th style={{ width: "22%" }}>Purpose</th>
                    <th style={{ width: "24%" }}>Description</th>
                    <th style={{ width: "12%" }}>Legal Basis</th>
                    <th style={{ width: "14%" }}>Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistry.length > 0 ? (
                    filteredRegistry.map((row, i) => (
                      <tr key={i}>
                        <td className="gdpr-table__title">
                          <strong>{row.dataset}</strong>
                          <span className="gdpr-table__storage">{row.storage}</span>
                        </td>
                        <td className="gdpr-table__source">{row.source}</td>
                        <td className="gdpr-table__purpose">{row.purpose}</td>
                        <td className="gdpr-table__desc">{row.description}</td>
                        <td>
                          <span
                            className={`legal-badge ${
                              row.basis.toLowerCase().includes("contract")
                                ? "legal-badge--blue"
                                : "legal-badge--purple"
                            }`}
                          >
                            {row.basis}
                          </span>
                        </td>
                        <td className="gdpr-table__retention">{row.retention}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="gdpr-table__empty">
                        No matching records found for "{searchTerm}".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <h2>Timeline for Retaining Users PII</h2>
          <p>
            Conectify Tech will retain PII for as long as we require it, including legally required duration and/or until
            you request us to delete or modify your PII. Please note that in case of a request for deletion of your
            information, Conectify Tech will retain some of your PII to ensure full compliance with your request.
          </p>

          <h2>Contact Us</h2>
          <p>
            If you have any additional questions regarding this Privacy Policy, please contact us at{" "}
            <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>. Any questions about this Privacy Policy
            should be addressed to <a href="mailto:info@conectify-tech.com">info@conectify-tech.com</a>.
          </p>

          {/* Location Cards */}
          <div className="legal-address-box">
            <div className="legal-address-card">
              <h4>Global Headquarters (USA)</h4>
              <p>1000 Brickell Ave Ste</p>
              <p>Miami, FL 33131</p>
              <p>USA</p>
              <p className="mt-2 text-primary font-semibold">+1 786 776 2152</p>
              <p className="text-primary font-semibold">info@conectify-tech.com</p>
            </div>
            <div className="legal-address-card">
              <h4>Operations &amp; Research Center</h4>
              <p>Mount Motor Gali, Ratu Road, Alkapuri</p>
              <p>Near Kidzee School, Ranchi - 834001</p>
              <p>Jharkhand, India</p>
              <p className="mt-2 text-primary font-semibold">info@conectify-tech.com</p>
            </div>
          </div>
        </article>
      </Container>
    </div>
  );
}